/* STORY-001 — the verification screen, in both of its jobs.
 *
 * Reached two ways, and the difference matters:
 *
 *   · straight after signing up, with no token — "check your email";
 *   · from the link in that email, with a token — confirm it.
 *
 * One route rather than two, because it is one place in the person's head and
 * the URL they are sent is the same one they were told to expect.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useTranslate } from '../../../shared/i18n/index.js';
import { AuthScreen, Button, Callout } from '../../../shared/ui/index.js';
import { useVerifyEmail } from '../api/index.js';

export function VerifyScreen() {
  const t = useTranslate();
  const navigate = useNavigate();
  const { token, email } = useSearch({ from: '/verify' });
  const verify = useVerifyEmail();

  /* Guards against React 18+ running effects twice in development. The token is
   * single-use, so a second call would consume it and report
   * `already_verified` — correct, but it would make the development experience
   * lie about what the server did. */
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    verify.mutate(token);
  }, [token, verify.mutate]);

  if (!token) return <CheckYourEmail email={email} />;

  if (verify.isPending || verify.isIdle) {
    return (
      <AuthScreen title={t('auth.verify.title')} brand={t('app.name')}>
        <p className="card__subtitle">{t('auth.verify.verifying')}</p>
      </AuthScreen>
    );
  }

  if (verify.isSuccess) {
    /* `verified` and `already_verified` are both success, and the screen does
     * not distinguish them. Clicking the link twice, or a mail scanner
     * prefetching it, must not look like a failure. */
    return (
      <AuthScreen title={t('auth.verify.verified_title')} brand={t('app.name')}>
        <Callout tone="success">{t('auth.verify.verified_body')}</Callout>
        <div className="auth__actions">
          {/* To SIGN IN, not into the app.
           *
           * Verifying an email does not authenticate anybody, and it must
           * not: a link that sits in an inbox and grants a session is a
           * security decision nobody made — the API deliberately issues no
           * cookie here.
           *
           * This said `/next` until 2026-08-29, when the first real signup on
           * production landed on the workspace chooser with no session. Every
           * request answered 401 and the screen rendered it as "Something
           * went wrong", which is what a new customer saw thirty seconds
           * after creating their account. */}
          <Button block onClick={() => void navigate({ to: '/sign-in' })}>
            {t('auth.verify.continue')}
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return <VerificationFailed error={verify.error} />;
}

function CheckYourEmail({ email }: { readonly email: string | undefined }) {
  const t = useTranslate();

  return (
    <AuthScreen title={t('auth.verify.title')} brand={t('app.name')}>
      <Callout tone="info">
        {/* The address is interpolated into a whole sentence, never concatenated
            around it. Turkish attaches suffixes to the word, so a sentence built
            from fragments in English word order cannot be reassembled. */}
        {email ? t('auth.verify.sent_to', { email }) : t('auth.verify.why')}
      </Callout>
      {email ? <p className="field__hint">{t('auth.verify.why')}</p> : null}
    </AuthScreen>
  );
}

function VerificationFailed({ error }: { readonly error: unknown }) {
  const t = useTranslate();

  if (error instanceof NetworkError) {
    return (
      <AuthScreen title={t('auth.verify.title')} brand={t('app.name')}>
        <Callout tone="danger">{t('error.network')}</Callout>
      </AuthScreen>
    );
  }

  /* Branching on the CODE. An earlier version of this line sniffed the English
   * message for the word "expired", which is precisely the parsing the error
   * scheme forbids — and it would have broken the moment the copy changed or a
   * localised message arrived. The API gained `token_expired` and
   * `token_invalid` so this could be a real branch. */
  const expired = error instanceof ApiError && error.code === 'token_expired';

  return (
    <AuthScreen
      title={expired ? t('auth.verify.expired_title') : t('auth.verify.invalid_title')}
      brand={t('app.name')}
    >
      <Callout tone="danger">
        {expired ? t('auth.verify.expired_body') : t('auth.verify.invalid_body')}
      </Callout>
      <div className="auth__actions">
        <Link className="btn btn--secondary btn--block" to="/sign-in">
          {t('auth.verify.go_to_sign_in')}
        </Link>
      </div>
    </AuthScreen>
  );
}
